import { z } from 'zod'

/**
 * Schema for the WYSIWYG header/footer "design" document that replaces the
 * legacy toggle-based `customize` preferences. Shared by the client editor,
 * the server preferences route, and the HTML/Puppeteer render pipeline so
 * there is exactly one definition of the shape.
 */

export const DESIGN_SCHEMA_VERSION = 1 as const

// CSS color strings feed directly into inline `style` attributes server-side
// (Puppeteer), so this is intentionally restrictive rather than accepting any
// string: hex (#rgb / #rrggbb / #rrggbbaa), rgb()/rgba(), or `transparent`.
export const designColorSchema = z
  .string()
  .regex(
    /^(#[0-9A-Fa-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)|transparent)$/,
    'Invalid color value'
  )

export const pageSizeKeySchema = z.enum(['letter', 'a4'])
export type PageSizeKey = z.infer<typeof pageSizeKeySchema>

export const PAGE_SIZES: Record<
  PageSizeKey,
  { w: number; h: number; label: string }
> = {
  letter: { w: 816, h: 1056, label: 'Letter (8.5×11)' },
  a4: { w: 800, h: 1122, label: 'A4 (210×297mm)' },
}

export const pageModeSchema = z.enum(['all', 'firstOnly', 'firstAndSubsequent'])
export type PageMode = z.infer<typeof pageModeSchema>

const baseGeometrySchema = {
  id: z.string().min(1),
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  rotation: z.number().min(-360).max(360),
}

export const borderSideSchema = z.object({
  width: z.number().min(0).max(50),
  style: z.enum(['solid', 'dashed', 'dotted', 'none']),
  color: designColorSchema,
})
export type BorderSide = z.infer<typeof borderSideSchema>

// TextBlock.content is sanitized HTML (see sanitize-design-html.ts) and may
// contain design tokens like `{{recruiter.name}}` resolved at render time.
export const textBlockSchema = z.object({
  ...baseGeometrySchema,
  type: z.literal('text'),
  content: z.string().max(20_000),
  fontSize: z.number().min(1).max(500),
  fontFamily: z.string().min(1).max(255),
  textAlign: z.enum(['left', 'center', 'right', 'justify']),
  lineHeight: z.number().min(0.5).max(5),
  color: designColorSchema,
})
export type TextBlock = z.infer<typeof textBlockSchema>

export const watermarkBlockSchema = textBlockSchema
  .omit({ type: true, id: true })
  .extend({
    type: z.literal('watermark'),
    id: z.literal('watermark-singleton'),
  })
export type WatermarkBlock = z.infer<typeof watermarkBlockSchema>

export const containerBlockSchema = z.object({
  ...baseGeometrySchema,
  type: z.literal('container'),
  backgroundColor: designColorSchema,
  borderLinked: z.boolean(),
  borderTop: borderSideSchema,
  borderRight: borderSideSchema,
  borderBottom: borderSideSchema,
  borderLeft: borderSideSchema,
  radiusLinked: z.boolean(),
  radiusTL: z.number().min(0),
  radiusTR: z.number().min(0),
  radiusBR: z.number().min(0),
  radiusBL: z.number().min(0),
  padding: z.number().min(0),
})
export type ContainerBlock = z.infer<typeof containerBlockSchema>

// `assetKey` is the sole persisted image reference, resolved via
// AgencyAssetService at render time. Inline base64 `src` is never part of
// the validated/persisted shape — the client editor's local state may carry
// an ephemeral local-preview URL while an upload is in flight, but it must
// be stripped before a design is saved through this schema.
export const imageBlockSchema = z.object({
  ...baseGeometrySchema,
  type: z.literal('image'),
  assetKey: z.string().min(1).optional(),
  originalWidth: z.number().min(0),
  originalHeight: z.number().min(0),
  // Corner rounding masks the image (the outer box clips), so a large
  // enough radius turns a square image circular. Optional for backward
  // compatibility with designs saved before rounding existed (absent = 0).
  radiusLinked: z.boolean().optional(),
  radiusTL: z.number().min(0).optional(),
  radiusTR: z.number().min(0).optional(),
  radiusBR: z.number().min(0).optional(),
  radiusBL: z.number().min(0).optional(),
  // How the image fills its box. Defaults to 'fill' (stretch), which is
  // exact for editor uploads because their box starts at the image's
  // natural size. Converted legacy designs use 'contain' — the converter
  // doesn't know the image's dimensions, so an aspect-preserving fit is
  // the only way to avoid distorting logos (and matches the legacy
  // template's background-size: contain).
  fit: z.enum(['fill', 'contain']).optional(),
})
export type ImageBlock = z.infer<typeof imageBlockSchema>

export const resumeBlockSchema = z.object({
  ...baseGeometrySchema,
  type: z.literal('resume'),
})
export type ResumeBlock = z.infer<typeof resumeBlockSchema>

export const headerComponentSchema = z.discriminatedUnion('type', [
  textBlockSchema,
  watermarkBlockSchema,
  containerBlockSchema,
  imageBlockSchema,
  resumeBlockSchema,
])
export type HeaderComponent = z.infer<typeof headerComponentSchema>

export const designPageSchema = z.object({
  components: z.array(headerComponentSchema),
})

export const savedDesignSchema = z.object({
  version: z.literal(DESIGN_SCHEMA_VERSION),
  pageSize: pageSizeKeySchema,
  pageMode: pageModeSchema,
  firstPage: designPageSchema,
  subsequentPages: designPageSchema,
})
export type SavedDesign = z.infer<typeof savedDesignSchema>
